import { v4 as uuidv4 } from 'uuid';
import { assocPath, pathOr, always } from 'rambda';
import { compose as lcompose, map as lmap, isEmpty } from 'lodash/fp';
import { Either, left, right, fold, map as emap } from 'fp-ts/lib/Either';
import { flow } from 'fp-ts/function';
import { ICert, ICerts, ILegacyRevokeRoles } from './types';
import { Dispatch } from 'react';
import { TeamRequestAction } from '../team-request';
import {
  csosUpmApi,
  ErrorCode,
  IError,
  IUser,
  ResponseCSOSHeaders,
} from '../common';

export const revokeRoles =
  (hR: ResponseCSOSHeaders) => async (input: ILegacyRevokeRoles) =>
    csosUpmApi(hR)
      .post(`v1/api/admin/legacy-request`, input)
      .then(r => right(r.data))
      .catch(e => left(e));

export const saveRequest =
  (dispatchSaveRequest: ({ type, payload }: TeamRequestAction) => void) =>
  (user: IUser) =>
    flow(
      getRequest,
      emap<ILegacyRevokeRoles, Promise<void>>(
        handleRequest(user.icam!.headers)(dispatchSaveRequest)
      )
    );

export const getRequest = right<undefined, ILegacyRevokeRoles>;

export const handleRequest =
  (hR: ResponseCSOSHeaders) =>
  (dispatchRequest: Dispatch<TeamRequestAction>) =>
  (request: ILegacyRevokeRoles) =>
    revokeRoles(hR)(request).then(
      fold<IError, void, void>(
        () =>
          dispatchRequest({
            type: 'SaveRequestError',
            payload: { code: ErrorCode.SaveRequestError, message: '' },
          }),
        b => {
          dispatchRequest({
            type: 'SaveRequestSuccess',
            payload: void 0,
          });
        }
      )
    );

const filter = ({
  email,
  deaNumber,
  serialNumber,
  page,
  size,
}: {
  email: string | null;
  deaNumber: string | null;
  serialNumber: string | null;
  page: number;
  size: number;
}) =>
  !isEmpty(email)
    ? `?email=${email}&page=${page}&size=${size}`
    : !isEmpty(deaNumber)
    ? `?deaNumber=${deaNumber}&page=${page}&size=${size}`
    : `?serialNumber=${serialNumber}&page=${page}&size=${size}`;

export const getLegacyCerts =
  (hR: ResponseCSOSHeaders) =>
  ({
    email,
    deaNumber,
    serialNumber,
    page,
    size,
  }: {
    email: string | null;
    deaNumber: string | null;
    serialNumber: string | null;
    page: number;
    size: number;
  }): Promise<Either<IError, ICerts>> =>
    flow(filter, a =>
      csosUpmApi(hR)
        .get(`v1/api/admin/legacy-certificate-search${a}`)
        .then(handleUserRolesSuccess)
        .catch(e => left(e as IError))
    )({
      email,
      deaNumber,
      serialNumber,
      page,
      size,
    });

const handleUserRolesSuccess = lcompose(
  right,
  // left,
  a => ({
    ...a,
    content: lmap<any, ICert>(a => ({ ...a, key: uuidv4() }))(a.content),
  }),
  pathOr<ICerts>({ total: 0, content: [] }, 'data')
);
